package com.sddamico.swigmore.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import com.sddamico.swigmore.api.PollApi
import com.sddamico.swigmore.components.layouts.Column
import com.sddamico.swigmore.components.layouts.Columns
import com.sddamico.swigmore.components.layouts.ContentColumn
import com.sddamico.swigmore.components.layouts.SignedInPage
import com.sddamico.swigmore.ds.Reveal
import com.sddamico.swigmore.ds.SwColor
import com.sddamico.swigmore.ds.core.Card
import com.sddamico.swigmore.ds.core.CardVariant
import com.sddamico.swigmore.ds.core.PageTitle
import com.sddamico.swigmore.ds.core.PanelLabel
import com.sddamico.swigmore.ds.css
import com.sddamico.swigmore.ds.feedback.Banner
import com.sddamico.swigmore.ds.feedback.Countdown
import com.sddamico.swigmore.ds.feedback.CountdownTone
import com.sddamico.swigmore.ds.feedback.PageTitleSkeleton
import com.sddamico.swigmore.ds.feedback.Skeleton
import com.sddamico.swigmore.ds.feedback.SkeletonBlock
import com.sddamico.swigmore.ds.forms.Button
import com.sddamico.swigmore.ds.forms.ButtonStyle
import com.sddamico.swigmore.ds.forms.ButtonVariant
import com.sddamico.swigmore.ds.forms.TextField
import com.sddamico.swigmore.ds.forms.TextFieldVariant
import com.sddamico.swigmore.ds.poll.BallotReviewCard
import com.sddamico.swigmore.ds.poll.BallotSlot
import com.sddamico.swigmore.ds.poll.FieldTeamRow
import com.sddamico.swigmore.ds.poll.ReviewNoteView
import com.sddamico.swigmore.ds.poll.PreviousPickRow
import com.sddamico.swigmore.ds.poll.SlotDragHandlers
import com.sddamico.swigmore.ds.typeBody
import com.sddamico.swigmore.ds.typeCaps
import com.sddamico.swigmore.ds.typeDisplay
import com.sddamico.swigmore.ds.typeMono
import com.sddamico.swigmore.models.BallotReviewResponse
import com.sddamico.swigmore.models.BallotStatus
import com.sddamico.swigmore.models.CurrentPollResponse
import com.sddamico.swigmore.models.GameOutcome
import com.sddamico.swigmore.models.MyBallotDto
import com.sddamico.swigmore.models.PollRules
import com.sddamico.swigmore.models.PollStanding
import com.sddamico.swigmore.models.PollTeamDto
import com.sddamico.swigmore.models.PollWeekDto
import com.sddamico.swigmore.models.ReviewNoteKind
import com.sddamico.swigmore.models.PreviousBallotDto
import com.sddamico.swigmore.models.WeekStatus
import com.sddamico.swigmore.util.AppTab
import com.sddamico.swigmore.util.BallotDrafts
import com.sddamico.swigmore.util.CachedResource
import com.sddamico.swigmore.util.LoadState
import com.sddamico.swigmore.util.NOT_FINAL
import com.sddamico.swigmore.util.against
import com.sddamico.swigmore.util.deadlineTime
import com.sddamico.swigmore.util.fixed
import com.sddamico.swigmore.util.letter
import com.sddamico.swigmore.util.rememberAction
import com.sddamico.swigmore.util.rememberApiResource
import com.sddamico.swigmore.util.rememberCachedResource
import com.sddamico.swigmore.util.rememberNow
import com.sddamico.swigmore.util.shortDayTime
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.navigation.Anchor
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import kotlin.js.Date
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** "Pick your eight.": the signed-in owner's Top 8 for the open week, with submit, amend and withdraw. */
@Page
@Composable
fun BallotPage() {
  SignedInPage("My Ballot", activeTab = AppTab.Ballot) { signedIn ->
    ContentColumn { BallotLoader(signedIn.me.id) }
  }
}

@Composable
private fun BallotLoader(userId: String) {
  when (val current = rememberApiResource { PollApi.current() }.state) {
    LoadState.Loading -> BallotSkeleton()
    is LoadState.Failed -> Reveal { PageTitle("My ballot", "Couldn't load your ballot.", lede = current.message) }
    is LoadState.Ready -> Reveal {
      val week = current.value.week
      when {
        week == null -> PageTitle("My ballot", "No poll is open yet.",
          lede = "Ballots open once the commissioner starts this week's poll.")
        !current.value.canVote -> PageTitle("Week ${week.week} ballot", "Owners only.",
          lede = "Only league owners vote. Ask the commissioner to link this email to your Fantrax team.")
        else -> BallotEditor(userId, current.value, week)
      }
    }
  }
}

/** Null on the season's first poll, which has no last week. */
private fun lastWeekLabel(lastWeek: Map<String, PollStanding>?, teamId: String): String? = lastWeek?.let {
  when (val standing = it[teamId]) {
    is PollStanding.Ranked -> "#${standing.rank}"
    PollStanding.ReceivingVotes -> "RV"
    PollStanding.Unranked, null -> "NR"
  }
}

@Composable
private fun BallotEditor(userId: String, current: CurrentPollResponse, week: PollWeekDto) {
  val request = rememberAction()
  val teams = remember(current) { current.teams.associateBy { it.id } }
  var saved by remember { mutableStateOf(current.myBallot) }
  val draft = remember {
    val submitted = slotsOf(current.myBallot)
    // A draft edited from a ballot that has since changed (the commissioner edited it, or the owner
    // voted from another device) would hide the change, so only one built on this ballot comes back.
    val held = BallotDrafts.get(userId, week.season, week.week)?.takeIf { it.base == submitted }
    mutableStateListOf<String?>().apply { addAll(held?.slots ?: submitted) }
  }
  LaunchedEffect(Unit) {
    snapshotFlow { BallotDrafts.Draft(base = slotsOf(saved), slots = draft.toList()) }.collect {
      if (it.slots == it.base) BallotDrafts.remove(userId, week.season, week.week)
      else BallotDrafts.put(userId, week.season, week.week, it)
    }
  }
  var amending by remember { mutableStateOf(false) }
  var dragSlot by remember { mutableStateOf<Int?>(null) }
  var dragTeam by remember { mutableStateOf<String?>(null) }
  var over by remember { mutableStateOf<Int?>(null) }
  var query by remember { mutableStateOf("") }
  var deadlinePassed by remember { mutableStateOf(Date.now() >= week.closesAt) }
  val reviewing = rememberAction()
  var review by remember { mutableStateOf<ReviewedBallot?>(null) }
  fun resetDraft() {
    draft.clear()
    draft.addAll(slotsOf(saved))
    amending = false
  }
  LaunchedEffect(week.closesAt) {
    delay((week.closesAt - Date.now()).toLong().coerceAtLeast(0))
    deadlinePassed = true
    // Unsubmitted edits don't count, so the locked page shows the ballot that does.
    resetDraft()
  }

  val locked = week.status != WeekStatus.OPEN || deadlinePassed
  val full = draft.all { it != null }
  val missing = draft.count { it == null }
  val showSubmitted = saved != null && draft.toList() == saved?.teamIds && !amending
  val dragging = dragSlot != null || dragTeam != null

  fun endDrag() {
    dragSlot = null
    dragTeam = null
    over = null
  }

  fun withdraw() = request.run {
    PollApi.withdrawBallot()
    saved = null
    resetDraft()
  }

  Div(
    attrs = Modifier.css("display" to "flex", "flex-wrap" to "wrap", "justify-content" to "space-between",
      "align-items" to "flex-end", "gap" to "16px", "margin-bottom" to "22px").toAttrs()
  ) {
    PageTitle(
      eyebrow = listOfNotNull("Week ${week.week} ballot", current.myTeamId?.let { teams[it]?.name }).joinToString(" · "),
      title = "Pick your eight.",
      lede = when {
        week.status == WeekStatus.PUBLISHED -> "Ballots are locked. The Week ${week.week} poll is out."
        locked -> "Ballots are locked. The Week ${week.week} poll is being tallied."
        else -> "Drag to reorder, or use the arrows. Ballots are anonymous and can be amended or withdrawn until the whistle."
      },
      modifier = Modifier.css("max-width" to "560px"),
    )
    if (!locked) DeadlineCountdown(week.closesAt)
  }

  val ballot = saved
  if (showSubmitted && ballot != null) {
    val verb = if (ballot.status == BallotStatus.AMENDED) "Amended" else "Submitted"
    Banner(
      "Your ballot is in.",
      body = if (locked) "$verb ${shortDayTime(ballot.updatedAt)}."
      else "$verb ${shortDayTime(ballot.updatedAt)}. Change your mind? You've got until ${deadlineTime(week.closesAt)}.",
      modifier = Modifier.css("margin-bottom" to "18px"),
    ) {
      if (!locked) {
        Button("Amend", style = ButtonStyle.Caps, disabled = request.busy, onClick = { amending = true })
        Button("Withdraw", variant = ButtonVariant.Outline, style = ButtonStyle.Caps, disabled = request.busy, onClick = { withdraw() })
      }
    }
  } else if (ballot != null) {
    Banner(
      "Your submitted ballot still counts.",
      body = "Submit to replace it with these changes, or discard them.",
      modifier = Modifier.css("margin-bottom" to "18px"),
    ) {
      Button("Discard changes", style = ButtonStyle.Caps, disabled = request.busy, onClick = { resetDraft() })
      Button("Withdraw", variant = ButtonVariant.Outline, style = ButtonStyle.Caps, disabled = request.busy, onClick = { withdraw() })
    }
  }

  val previous = current.previousBallot?.takeUnless { locked }
  // Copying replaces the slots, so it's offered only before the owner has placed a team.
  val copyPrevious = previous?.takeIf { draft.all { it == null } }?.let { p -> { p.picks.forEachIndexed { i, pick -> draft[i] = pick.teamId } } }
  Columns {
    Column("520px", Modifier.css("display" to "flex", "flex-direction" to "column", "gap" to "7px")) {
      previous?.let { PreviousBallotCard(it, teams, copyPrevious, PreviousBallotAboveStyle.toModifier()) }
      draft.forEachIndexed { i, teamId ->
        val team = teamId?.let { teams[it] }
        BallotSlot(
          position = i + 1,
          team = team?.name ?: teamId,
          meta = teamId?.let { listOfNotNull(team?.record?.toString() ?: "—", lastWeekLabel(current.lastWeek, it)?.let { lw -> "LW $lw" }).joinToString(" · ") },
          dropTarget = over == i && dragging,
          dragging = dragSlot == i,
          locked = locked,
          canMoveUp = i > 0,
          canMoveDown = i < PollRules.BALLOT_SIZE - 1,
          onMoveUp = { draft.swap(i, i - 1) },
          onMoveDown = { draft.swap(i, i + 1) },
          onRemove = { draft[i] = null },
          drag = SlotDragHandlers(
            onDragStart = { dragSlot = i },
            onDragOver = { over = it },
            onDrop = { target ->
              val from = dragSlot
              val incoming = dragTeam
              if (from != null) draft.add(target, draft.removeAt(from))
              else if (incoming != null) draft[target] = incoming
              endDrag()
            },
            onDragEnd = ::endDrag,
          ),
        )
      }
      Div(attrs = Modifier.css("display" to "flex", "flex-wrap" to "wrap", "gap" to "10px", "align-items" to "center", "margin-top" to "12px").toAttrs()) {
        Button(
          when {
            locked -> "Ballots Locked"
            showSubmitted -> "Submitted ✓"
            full -> "Submit Ballot"
            else -> "Fill $missing more slot${if (missing == 1) "" else "s"}"
          },
          Modifier.css("flex" to "1 1 220px"),
          variant = ButtonVariant.Gold,
          disabled = locked || showSubmitted || !full || request.busy,
          onClick = {
            request.run {
              saved = PollApi.submitBallot(draft.filterNotNull())
              amending = false
            }
          },
        )
        if (!locked && current.canReviewBallot) Button(if (reviewing.busy) "Reviewing…" else "Review ballot", variant = ButtonVariant.Outline,
          ai = true, sizedFor = "Review ballot", style = ButtonStyle.Caps, onDark = true, disabled = reviewing.busy || draft.all { it == null }, onClick = {
            val slots = draft.toList()
            reviewing.run(writes = false) { review = ReviewedBallot(slots, PollApi.reviewBallot(slots)) }
          })
        if (!locked) Button("Clear", variant = ButtonVariant.Ghost, style = ButtonStyle.CapsSm, onDark = true,
          disabled = request.busy, onClick = { draft.fill(null) })
      }
      listOfNotNull(request.error, reviewing.error).forEach {
        Div(attrs = Modifier.typeMono("11px").css("color" to SwColor.gold).toAttrs()) { Text(it) }
      }
      review?.takeIf { !locked }?.let { (slots, result) ->
        BallotReviewCard(
          result.summary,
          result.notes.map { ReviewNoteView(it.kind.label, it.text) },
          Modifier.css("margin-top" to "12px"),
          stale = slots != draft.toList(),
        )
      }
    }
    if (!locked) {
      Column("320px") {
        val pool = current.teams.filter { it.id !in draft }.sortedByDescending { it.record?.wins ?: -1 }
        val shown = pool.filter { it.name.contains(query.trim(), ignoreCase = true) }
        previous?.let { PreviousBallotCard(it, teams, copyPrevious, PreviousBallotBesideStyle.toModifier()) }
        Card(variant = CardVariant.Field) {
          PanelLabel("The field · ${pool.size} unranked", note = "BY RECORD")
          TextField(query, { query = it }, Modifier.css("margin-bottom" to "10px"), variant = TextFieldVariant.Search,
            placeholder = "Search teams…")
          Div(attrs = Modifier.css("display" to "flex", "flex-direction" to "column", "gap" to "3px",
            "max-height" to "520px", "overflow" to "auto").toAttrs()) {
            shown.forEach { t ->
              FieldTeamRow(
                t.name, t.record?.toString() ?: "—", lastWeekLabel(current.lastWeek, t.id),
                onAdd = { if (t.id !in draft) draft.indexOf(null).takeIf { it >= 0 }?.let { draft[it] = t.id } },
                onDragStart = { dragTeam = t.id },
                onDragEnd = ::endDrag,
              )
            }
          }
        }
        ComputerTopEight(Modifier.css("margin-top" to "16px"))
      }
    }
  }
}

/** The owner's ballot from the poll before, with how each team did that week; [onCopy] is offered only while the slots are empty. */
@Composable
private fun PreviousBallotCard(
  previous: PreviousBallotDto,
  teams: Map<String, PollTeamDto>,
  onCopy: (() -> Unit)?,
  modifier: Modifier = Modifier,
) {
  Card(Modifier.css("margin-bottom" to "16px").then(modifier), variant = CardVariant.Field) {
    PanelLabel("Your week ${previous.week} ballot", note = "HOW THEY DID")
    previous.picks.forEachIndexed { i, pick ->
      val game = pick.game
      PreviousPickRow(
        position = i + 1,
        team = teams[pick.teamId]?.name ?: pick.teamId,
        result = game?.let { it.outcome.against(teams[it.opponentId]?.name ?: it.opponentId) } ?: "Scores not in yet",
        score = game?.let {
          listOfNotNull("${fixed(it.score, 1)}–${fixed(it.opponentScore, 1)}", NOT_FINAL.takeUnless { _ -> it.final }).joinToString(" · ")
        },
        outcome = game?.outcome?.letter,
        won = game?.outcome == GameOutcome.WIN,
      )
    }
    if (onCopy != null) {
      Button("Start from week ${previous.week}", Modifier.css("margin-top" to "10px"),
        variant = ButtonVariant.Cream, style = ButtonStyle.CapsSm, fullWidth = true, onClick = onCopy)
    }
  }
}

/** Beside the field when the columns sit side by side, above the slots once they stack. */
val PreviousBallotBesideStyle = CssStyle {
  base { Modifier.css("display" to "none") }
  Breakpoint.LG { Modifier.css("display" to "block") }
}

val PreviousBallotAboveStyle = CssStyle {
  Breakpoint.LG { Modifier.css("display" to "none") }
}

/** The computer polls' top of the table while owners vote; hidden until the first week is final. */
@Composable
private fun ComputerTopEight(modifier: Modifier) {
  val poll = (rememberCachedResource(CachedResource.ComputerPoll).state as? LoadState.Ready)?.value ?: return
  if (poll.rows.isEmpty()) return
  Card(modifier, variant = CardVariant.Field) {
    val ranking = if (poll.coachesPollWeek == null) "COLLEY" else "BCS"
    PanelLabel("Computer polls", note = listOfNotNull(ranking, poll.throughWeek?.let { "THROUGH WK $it" }).joinToString(" · "))
    poll.rows.take(PollRules.BALLOT_SIZE).forEach { row ->
      Div(
        attrs = Modifier.css("display" to "grid", "grid-template-columns" to "28px minmax(0,1fr) auto", "gap" to "10px",
          "align-items" to "center", "padding" to "6px 4px", "border-top" to "1px solid ${SwColor.hairlineSoft}").toAttrs()
      ) {
        Span(attrs = Modifier.typeDisplay("16px").toAttrs()) { Text(row.rank.toString()) }
        Span(attrs = Modifier.typeBody("14px", 600, "normal").toAttrs()) { Text(row.team.name) }
        Span(attrs = Modifier.typeMono("11px").css("color" to SwColor.textMuted).toAttrs()) { Text(row.record.toString()) }
      }
    }
    Anchor(href = AppTab.ComputerPolls.path, attrs = Modifier.typeCaps("10px", ".16em").css("display" to "block", "margin-top" to "10px",
      "text-decoration" to "none").toAttrs()) { Text("Full computer polls →") }
  }
}

/** Ticks once a second on its own so the rest of the ballot doesn't recompose with it. */
@Composable
private fun DeadlineCountdown(closesAt: Long) {
  Countdown(closesAt - rememberNow(), tone = CountdownTone.OnField)
}

/** A review and the [slots] it was asked about. */
private data class ReviewedBallot(val slots: List<String?>, val review: BallotReviewResponse)

private val ReviewNoteKind.label get() = when (this) {
  ReviewNoteKind.CHANGE -> "Since last week"
  ReviewNoteKind.OUTLIER -> "Against the polls"
  ReviewNoteKind.RESULT -> "Results"
}

private fun slotsOf(ballot: MyBallotDto?): List<String?> = ballot?.teamIds ?: List(PollRules.BALLOT_SIZE) { null }

private fun MutableList<String?>.swap(a: Int, b: Int) {
  this[a] = this[b].also { this[b] = this[a] }
}

@Composable
private fun BallotSkeleton() {
  Skeleton {
    PageTitleSkeleton(Modifier.css("max-width" to "560px", "margin-bottom" to "22px"))
    Columns {
      Column("520px", Modifier.css("display" to "flex", "flex-direction" to "column", "gap" to "7px")) {
        repeat(PollRules.BALLOT_SIZE) { SkeletonBlock("56px") }
      }
      Column("320px") { SkeletonBlock("520px") }
    }
  }
}
